import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as moment from "moment";
import { LocalStorage } from 'ngx-store';
import { Observable, throwError } from 'rxjs';
import { catchError, pluck, retry, shareReplay, tap } from 'rxjs/operators';
import { Run, RunDriver, RunEquipment, RunPlanActivity, RunRoute, RunType } from '../model/dispatch.model';
import { ServiceType } from '../model/fleet.model';
import { BreachRequest } from '../pages/breach/breach';
import { ServiceTypeAbbreviations } from '../pages/dispatch/service-type-abbreviations.enum';


@Injectable({
    providedIn: 'root'
})
export class DispatchService {
    @LocalStorage() url;
    // Http Headers
    httpOptions = {
        headers: new HttpHeaders({
            'Content-Type': 'application/json'
        })
    }
    
    driverLicenceClasses = {
      CAR: 1, LR: 2, MR: 3,
      HR: 4, HC: 5, MC: 7
    }

    constructor(public http: HttpClient) { }

    /**
     * Dispatch options.
     */
    private readonly dispatchOptions$ = this.getDispatchMiscData().pipe(shareReplay(1));

    /**
     * List of service types.
     */
    public readonly serviceTypes$ = this.dispatchOptions$.pipe(pluck('service_types'));

    /**
     * List of run types.
     */
    public readonly runTypes$ = this.dispatchOptions$.pipe(pluck('run_types'));

    public readonly runRoutes$ = this.dispatchOptions$.pipe(pluck('state_routes'));

    getRuns(statusFilter = '', dateFilter = '', start = '', end = ''): Observable<any> {
        var param = '';
        if (statusFilter) {
            param = '/' + statusFilter;
            if (dateFilter) {
                param = param + '/' + dateFilter;
                if (dateFilter == 'range') {
                    if (moment(end).isSameOrAfter(moment(start))) {
                        param = param + '/' + start + '/' + end; 
                    }
                } 
            }

        }
        console.log('param', param);
        //-> to include "param" in the request
        return this.http.get<any>(this.url.rest + '/tms/getRuns' + param, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            );
    }

    //for linehaul manifestv2 testing
    getRunsv2(statusFilter, dateFilter, start, end, offset, limit){
        var param = '';
        let status = statusFilter?statusFilter:0;
        let date = dateFilter?dateFilter:0;
        let from = start?start:0;
        let to = end?end:0;
    
        param = '/'+status+'/'+date+'/'+from+'/'+to+'/'+offset+'/'+limit;
        console.log('param ', param);
    
        // return this.http.get<any>('http://127.0.0.1:8000/tms/getRunsv2' + param, this.httpOptions)
        return this.http.get<any>(this.url.rest+ '/tms/getRunsv2' + param, this.httpOptions)
        .pipe(
            // retry(1),
            catchError(this.handleError)
        );
    }

    private service_types: ServiceType[] = [];
    getDispatchMiscData() {
        return this.http.get<{
            service_types: ServiceType[],
            run_types: RunType[],
            state_routes: RunRoute[]
        }>(this.url.rest + '/tms/getDispatchMiscData', this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError),
                tap(data => {
                    this.service_types = data.service_types;
                })
            );
    }

    getDispatchLocationData(): Observable<any> {
        return this.http.get<any>(this.url.rest + '/tms/getDispatchLocationData', this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            );
    }

    getDrivers(): Observable<any> {
        return this.http.get<any>(this.url.rest + '/tms/getDispatchDrivers', this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            );
    }

    getEquipments(): Observable<any> {
        return this.http.get<any>(this.url.rest + '/tms/getEquipmentsForDispatch', this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            );
    }

    getRunById(run_id): Observable<any> {
        return this.http.get<any>(this.url.rest + '/tms/getRunById/' + run_id, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            );
    }

    getActivitiesv2(run_type_id): Observable<any> {
        return this.http.get<any>(this.url.rest + '/tms/getActivitiesByRunId/' + run_type_id, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            );
    }

    getActivitiesv3(run_type_id): Observable<any> {
        return this.http.get<any>(this.url.rest + '/tms/getActivitiesByRunIdv2/' + run_type_id, this.httpOptions)
            .pipe(
                // retry(1),
                catchError(this.handleError)
            );
    }

    getActivities(service_type_id): Observable<any> {
        return this.http.get<any>(this.url.rest + '/tms/getActivities/' + service_type_id, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            );
    }

    getActivityTasks(service_type_id): Observable<any> {
        return this.http.get<any>(this.url.rest + '/tms/getTasksWithoutRun/' + service_type_id, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            );
    }


    getActivityTasks_warehouse(service_type_id): Observable<any> {
        return this.http.get<any>(this.url.rest + '/tms/getTasksWithoutRunWarehouse/' + service_type_id, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            );
    }

    getPresets(connote_id): Observable<any> {
        return this.http.get<any>(this.url.rest + '/tms/getPresetDriverEquipment/' + connote_id, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            );
    }

    getDriverDispatchAssignments(driver_id): Observable<any> {
        return this.http.get<any>(this.url.rest + '/tms/getDriverDispatchAssignments/' + driver_id, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            );
    }

    getMinimumRestHrs(hours_data): Observable<any> {
        return this.http.get<any>(`${this.url.rest}/tms/getMinimumRestHrs?total_work_hours=${hours_data.total_work_hours}&driver_standard=${hours_data.driver_standard}`, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            );
    }

    getMinimumRestHrsKlms(total_klms): Observable<any> {
        return this.http.get<any>(this.url.rest + '/tms/getDrivingHoursFrTotalKlms/' + total_klms, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            );
    }

    getEquipmentDispatchAssignments(fleet_id): Observable<any> {
        return this.http.get<any>(this.url.rest + '/tms/getEquipmentsDispatchAssignments/' + fleet_id, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            );
    }

    getTotalKlmsHours(row){
        return this.http.post<any>(this.url.rest + '/tms/getGoogleTotalKlmsHours', row, this.httpOptions)
        .pipe(
            retry(1),
            catchError(this.handleError)
        );
    }

    getDriverTruckPairs(){
        return this.http.get<any>(this.url.rest + '/tms/driver-truck-pairs', this.httpOptions)
        .pipe(
            retry(1),
            catchError(this.handleError)
        );

    }

    createRoute(route): Observable<any> {
        return this.http.post<any>(this.url.rest + '/tms/createRoute', route, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )
    }

    createRuns(runs): Observable<any> { //latest createRun with changeover
        return this.http.post<any>(this.url.rest + '/tms/createRuns', runs, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )
    }

    createNewActivities(act): Observable<any> {
        return this.http.post<any>(this.url.rest + '/tms/createNewActivities', act, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )
    }

    sendLegacyDispatchMail(ids): Observable<any> {
        return this.http.post<any>(this.url.rest + '/tms/sendLegacyDispatchMail', ids, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )
    }

    sendDispatchMail(connoteId: string): Observable<string> {
        const params = {connote_id: connoteId};
        return this.http.post<string>(this.url.rest  + '/tms/sendDispatchMail', params, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    
    }

    manifestToggleDriverFitnessCompliance(flag: boolean, manifest_id: number) {
        const statusData = { flag: flag, manifest_id: manifest_id};
        return this.http.post<any>(this.url.rest + '/tms/manifestToggleDriverFitnessCompliance', statusData, this.httpOptions)
        .pipe(
            retry(1),
            catchError(this.handleError)
        )
    }

    manifestToggleFleetCheckCompliance(flag: boolean, manifest_id: number) {
        const statusData = { flag: flag, manifest_id: manifest_id};
        return this.http.post<any>(this.url.rest + '/tms/manifestToggleFleetCheckCompliance', statusData, this.httpOptions)
        .pipe(
            retry(1),
            catchError(this.handleError)
        )
    }

    manifestToggleChecklist(flag: boolean, run_id: number, user_id: number) {
        const statusData = { flag: flag, run_id: run_id, user_id: user_id};
        return this.http.post<any>(this.url.rest + '/tms/manifestToggleChecklist', statusData, this.httpOptions)
        .pipe(
            retry(1),
            catchError(this.handleError)
        )
    }


    manifestToggleWorkDiaryCompliance(flag: boolean, manifest_id: number) {
        const statusData = { flag: flag, manifest_id: manifest_id};
        return this.http.post<any>(this.url.rest + '/tms/manifestToggleWorkDiaryCompliance', statusData, this.httpOptions)
        .pipe(
            retry(1),
            catchError(this.handleError)
        )
    }

    manifestToggleFatigueRequirementCompliance(flag: boolean, manifest_id: number) {
        const statusData = { flag: flag, manifest_id: manifest_id};
        return this.http.post<any>(this.url.rest + '/tms/manifestToggleFatigueRequirementCompliance', statusData, this.httpOptions)
        .pipe(
            retry(1),
            catchError(this.handleError)
        )
    }

    manifestToggleLocationCheckCompliance(flag: boolean, manifest_id: number) {
        const statusData = { flag: flag, manifest_id: manifest_id};
        return this.http.post<any>(this.url.rest + '/tms/manifestToggleLocationCheckCompliance', statusData, this.httpOptions)
        .pipe(
            retry(1),
            catchError(this.handleError)
        )
    }


    manifestToggleLoadRestraintCheckCompliance(flag: boolean, manifest_id: number) {
        const statusData = { flag: flag, manifest_id: manifest_id};
        return this.http.post<any>(this.url.rest + '/tms/manifestToggleLoadRestraintCheckCompliance', statusData, this.httpOptions)
        .pipe(
            retry(1),
            catchError(this.handleError)
        )
    }

    manifestToggleMassWeightCheckCompliance(flag: boolean, manifest_id: number) {
        const statusData = { flag: flag, manifest_id: manifest_id};
        return this.http.post<any>(this.url.rest + '/tms/manifestToggleMassWeightCheckCompliance', statusData, this.httpOptions)
        .pipe(
            retry(1),
            catchError(this.handleError)
        )
    }

    updateEvent(row: any): Observable<any> {
        return this.http.post<any>(this.url.rest + '/tms/saveEvent', row, this.httpOptions)
        .pipe(
            retry(1),
            catchError(this.handleError)
        )
    }

      // run notes
      getDispatchNotes(id: number): Observable<any[]> {
        return this.http.get<any[]>(this.url.rest + '/tms/getDispatchNotes/' + id, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )
    }

    createLocation(values: any): Observable<any> {
        let location = {
            'lat': values.lat,
            'lng': values.long,
            'short_text': values.suburb+' '+values.state+' '+values.postal_code,
            'street_no': values.street_no,
            'street': values.street,
            'state': values.state,
            'suburb': values.suburb,
            'postal_code': values.postal_code
        }
        return this.http.post<any>(this.url.rest + '/tms/createLocation', location, this.httpOptions)
        .pipe(
            retry(1),
            catchError(this.handleError)
        )
    }

    createDispatchNote(note: any): Observable<any> {
        return this.http.post<any>(this.url.rest + '/tms/createDispatchNote', note, this.httpOptions)
        .pipe(
            retry(1),
            catchError(this.handleError)
        )
    }

    updateDispatchNote(note: any): Observable<any> {
        return this.http.post<any>(this.url.rest + '/tms/updateDispatchNote', note, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )
    }

    deleteDispatchNote(id: number): Observable<any[]> {
        return this.http.delete<any[]>(this.url.rest + '/tms/deleteDispatchNote/' + id, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )
    }
    
    generateSDP(run_id: number, user_id: number, trigger: string){
        const SDPdata = {run_id, trigger, user_id }
        return this.http.post<any>(this.url.rest + '/tms/generateSDP', SDPdata, this.httpOptions)
        .pipe(
            retry(1),
            catchError(this.handleError)
        )
    }

    cancelDispatch(data): Observable<any>{
        return this.http.post(this.url.rest + '/tms/cancelRun', data,this.httpOptions)
        .pipe(
            retry(1),
            catchError(this.handleError)
        )
    }

    undoChangeover(undo: any): Observable<any> {
        return this.http.post<any>(this.url.rest + '/tms/undoChangeover', undo, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )
    }

    getPickupPoints(postData: any): Observable<any[]> {
        return this.http.post<any[]>(this.url.rest  + '/tms/getPickupPoints', postData, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    
    }

    getDeliveryRunPoints(postData: any): Observable<any[]> {
        return this.http.post<any[]>(this.url.rest  + '/tms/getDeliveryRunPoints', postData, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    
    }

    addChangeover(postData: any): Observable<any[]> {
        return this.http.post<any[]>(this.url.rest  + '/tms/addChangeover', postData, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    
    }

    generateReleaseDocket(release_data:any): Observable<number> {
        return this.http.post<any>(this.url.rest + '/tms/generateReleaseDocket', release_data, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    
    }

    generateCollectDocket(collect_data:any): Observable<number> {
        return this.http.post<any>(this.url.rest + '/tms/generateCollectDocket', collect_data, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    
    }

    generateTripsheet(run_id: number, user_id: number){
        const tripData = {run_id, user_id }
        return this.http.post<any>(this.url.rest + '/tms/RegenerateTripSheet', tripData, this.httpOptions)
        .pipe(
            retry(1),
            catchError(this.handleError)
        )    
    }

    generateManifest(run_id: number){
        const tripData = {run_id}
        return this.http.post<any>(this.url.rest + '/tms/RegenerateManifest', tripData, this.httpOptions)
        .pipe(
            retry(1),
            catchError(this.handleError)
        )    
    }

    generateRunsheet(local_data:any): Observable<number> {
        return this.http.post<any>(this.url.rest + '/tms/generateRunsheet', local_data, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            )    
    }

    getLocalDispatchServiceTypeId(equipments){
        return this.http.post<any>(this.url.rest + '/tms/getLocalDispatchServiceTypeId', equipments, this.httpOptions)
        .pipe(
            retry(1),
            catchError(this.handleError)
        );
    }

    

    // Error handling
    handleError(error) {
        let errorMessage = '';
        if (error.error instanceof ErrorEvent) {
            // Get client-side error
            errorMessage = error.error.message;
        } else {
            // Get server-side error
            errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
        }
        console.log(errorMessage);
        return throwError(errorMessage);
    }

    assignmentConflict_v2(filteredAssignments, start_date: Date, end_date: Date){

        start_date = new Date(start_date); end_date = new Date(end_date) //converting to Date for accurrate comparison
        let lastAct = filteredAssignments.length-1;
        let drvformattedStartDate =  new Date(filteredAssignments[0].act[0].run_plan_start_datetime,);
        let drvformattedEndDate =  new Date(filteredAssignments[lastAct].act[filteredAssignments[lastAct].act.length-1].run_plan_start_datetime,);

        if((drvformattedEndDate < start_date  && drvformattedEndDate < end_date) 
            && (start_date > drvformattedStartDate &&  end_date > drvformattedStartDate)) return false;
        else return true;
    }

    assignmentConflict(current_assignments, start_date: Date, end_date: Date){
        let filteredAssignments = [], sortedArray = [], assignmentConflict = {}

        //grouping activities by run
        current_assignments.forEach(el => {sortedArray.push(el.run_id); });
        sortedArray = sortedArray.filter((element, i) => i === sortedArray.indexOf(element))
        sortedArray.forEach((sorted, i:number)=>{
          let arr = {'id': null , 'control_no':'', status:null, 'act':[]};
          arr.id = sorted;
          current_assignments.forEach(el => { if(el.run_id== sorted) arr.act.push(el); });
          arr.control_no = arr.act[0].run_control_no;
          arr.status = arr.act[arr.act.length-1].run_status
          filteredAssignments.push(arr);
        })

        start_date = new Date(start_date); end_date = new Date(end_date) //converting to Date for accurrate comparison
        let lastAct = filteredAssignments.length-1;
        let drvformattedStartDate =  new Date(filteredAssignments[0].act[0].run_plan_start_datetime,);
        let drvformattedEndDate =  new Date(filteredAssignments[lastAct].act[filteredAssignments[lastAct].act.length-1].run_plan_start_datetime,);
        
        assignmentConflict['current_assignments'] = filteredAssignments 
        assignmentConflict['will_finish'] = filteredAssignments[lastAct].act[filteredAssignments[lastAct].act.length-1].run_plan_end_datetime;

        if((drvformattedEndDate < start_date  && drvformattedEndDate < end_date) 
            && (start_date > drvformattedStartDate &&  end_date > drvformattedStartDate)) assignmentConflict['conflict'] = false;
        else assignmentConflict['conflict'] = true;

        console.log(assignmentConflict)
        return assignmentConflict;
    }

    /**
     * Checks if the service type matches the expected abbreviation based on service type ID
     * @param serviceTypeId The service type ID to check
     * @param expectedAbbreviation The expected abbreviation to compare with
     * @returns True if the service type matches the expected abbreviation, false otherwise
     */
    isServiceTypeMatch(serviceTypeId: number, expectedAbbreviation: string){
        const abbreviation = this.getAbbreviationById(serviceTypeId);
        return abbreviation === expectedAbbreviation;
    }

    /**
     * Retrieves the abbreviation of a service type based on its service type ID
     * @param serviceTypeId The service type ID for which to retrieve the abbreviation
     * @returns The abbreviation of the service type, or undefined if not found
     */
    getAbbreviationById(serviceTypeId: number){
        const matchingServiceType = this.service_types.find(record => record.id == serviceTypeId);
        return matchingServiceType?.abbreviation as ServiceTypeAbbreviations;
    }

    checkDriverLicence(service_type_id: number, driver_license_id: number){
      if (this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.UTE)
        || this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.Rigid)){ // cars
            return !!driver_license_id;
      } else if (this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.TwoTonne4P)
            || this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.SixTonne8P)
            || this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.TwelveTonne14P)
            || this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.TenPalletRigid) 
            || this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.TwoTonne6P) 
            || this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.SixTonne10P)
            || this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.EightTonne12P) 
            || this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.TwelveTonne12P)){  // Rigids
                return driver_license_id !== this.driverLicenceClasses.CAR;
      } else if (this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.SemiPMOnly) 
            || this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.SemiPMWithTrailers)
            || this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.BDoublePMOnly)
            || this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.BDoublePMWithTrailers)     
            || this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.RoadTrain)
            || this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.BDoublePMWithATrailer)
            || this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.BDoublePMWithBTrailer)){ //Semi and PM
                return driver_license_id === this.driverLicenceClasses.HC || driver_license_id === this.driverLicenceClasses.MC;
      }
      return false;
    }

    checkFleet_EquipmentType(service_type_id: number, fleet_type: number, fleet_subtype: number){
        let value = true;
        if(this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.UTE)
            || this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.Rigid)
        ){ //car
            if(fleet_type != 1 && fleet_type != 2) value = false;
        } else if (this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.SixTonne8P)
            || this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.TwelveTonne14P)
            || this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.TenPalletRigid) 
            || this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.TwoTonne6P) 
            || this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.SixTonne10P)
            || this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.EightTonne12P) 
            || this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.TwelveTonne12P)) { //rigids 
            if(fleet_type == 2){
              if(this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.SixTonne8P) 
                && (fleet_subtype != 7 
                    && fleet_subtype != 8 
                    && fleet_subtype != 10)) 
                        value = false;
              if(this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.TenPalletRigid) 
                && (fleet_subtype != 8 && fleet_subtype != 10)) 
                    value = false;
              if(this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.TwelveTonne14P) && fleet_subtype != 8) 
                value = false
            } else value = false;
        } else if (this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.SemiPMOnly) 
            || this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.SemiPMWithTrailers)){ //semi
                if(fleet_subtype != 1) value = false
        } else if(this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.BDoublePMOnly) 
            || this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.BDoublePMWithTrailers)
            || this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.ATrailer) 
            || this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.BTrailer)) { // prime movers
                if(fleet_type != 3 || fleet_subtype != 2) value = false;
        } else if (this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.RoadTrain)){ //road train
            if(fleet_type != 3 || fleet_subtype != 3) value = false; 
        }
        return value;
    }

    checkTrailer_EquipmentType(service_type_id: number, trailers,isAllowed:boolean){ //should send trailers as an array with fleet_type_id and fleet_subtype_id
        const tLength = trailers.length
        let value = true, error_message = '';

        if(isAllowed){
            value = true; error_message = "";
        }
        else{
             if(tLength !=0){
                    if(this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.SemiPMWithTrailers)){ // semi requires 1 Trailer (A or B)
                        if(tLength != 1){
                            value = false; error_message = "Only 1 Trailer is needed for this job"
                        }
                    } else if(this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.ATrailer)){ //requires 1 A trailer
                        if(tLength != 1 || trailers[0].fleet_subtype_id != 4){
                            value = false; error_message = "A Trailer is needed for this job";
                        }
                    }else if(this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.BTrailer)){ //requires 1 B trailer
                        if(tLength != 1 || trailers[0].fleet_subtype_id != 5){
                            value = false; error_message = "B Trailer is needed for this job";
                        }
                    } else if(this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.BDoublePMWithTrailers)){ //requires 2 trailers
                        if(tLength == 2){
                            let checkA = trailers.find(x => x.fleet_subtype_id == 4);
                            if(!checkA){ value = false;
                            } else {
                            let checkB =trailers.find(x => x.fleet_subtype_id == 5);
                            if (!checkA || !checkB) value = false;
                            }
                        } else if(tLength >2) { 
                            value = false; error_message = "1 A Trailer and 1 B Trailer are needed for this job."
                        } else {
                            value = false; error_message = "1 A Trailer and 1 B Trailer are needed for this job";
                        }
                        
                        if(value == false && tLength!=1) error_message = "1 A Trailer and 1 B Trailer are needed for this job"
                    } 
                    // else if(service == 10){ // min of 6 trailer
                    //     if(tLength < 6){
                    //         //update in road train now allows us any combination of trailer
                    //         value = false; error_message = "Only maximum of 6 Trailers are allowed."
                    //     }
                    // }
             } else {
                    value = false;
                    error_message = 'Trailers missing';

                    if(this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.RoadTrain)) value = true; //road train
             }
        }

        if(this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.UTE)
            || this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.Rigid)
            || this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.TwoTonne4P)
            || this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.SixTonne8P)
            || this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.TwelveTonne14P)
            || (this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.SemiPMOnly) 
                && !isAllowed) 
            || (this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.BDoublePMOnly)
                    && !isAllowed)  
            || this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.TenPalletRigid)
            || this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.TwoTonne6P)
            || this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.SixTonne10P)
            || this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.EightTonne12P)
            || this.isServiceTypeMatch(service_type_id, ServiceTypeAbbreviations.TwelveTonne12P)){ //services that don't need trailers
            if(tLength != 0){
                value = false; error_message = "No Trailers are needed for this job"
            } else {
                value = true; error_message = ""
            }
        }

        let result = { result: value, error: error_message}
        return result
    }

    
    getVolvo(volvo): Observable<any> {
        return this.http.get<any>(`${this.url.rest}/tms/getVolvo?run_id=${volvo.run_id}&vin=${volvo.vin}&user_id=${volvo.user_id}`, this.httpOptions)
            .pipe(
                retry(1),
                catchError(this.handleError)
            );
    }

    updatePlannedDetails(row: any): Observable<any> {
        if(row.connote['activities'])row.connote['activities'] =undefined;
        return this.http.post<any>(this.url.rest + '/tms/updatePlannedDetails', row, this.httpOptions)
        .pipe(
            retry(1),
            catchError(this.handleError)
        )
    }

    public updateEditedRun(runChanges){
        // console.log(JSON.stringify(runChanges));
        //'http://127.0.0.1:8000'
        return this.http.post<any>(this.url.rest + '/tms/updateEditedRun', runChanges, this.httpOptions)
        .pipe(
            retry(1),
            catchError(this.handleError)
        )

    }
}
